/**
 * CheckoutSummary — Paket B (premium-checkout-redesign)
 *
 * Linke Spalte von /checkout: Plan + Laufzeit-Segment + Preisaufschlüsselung
 * + Trust-Bullets. Design-Referenz: design/checkout.html (.co-wrap linke Spalte).
 *
 * Ehrlichkeitsregel: der Laufzeitrabatt wird NUR gezeigt, wenn der Gesamtpreis
 * aus durations[].price des Backends stammt (planMonthlyPrice.fromBackend) —
 * sonst wäre die Prozentangabe erfunden.
 */
import { Trans, useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import type { Plan } from '../subscription/useSubscriptionPlans'
import { displayPrice } from '../subscription/useSubscriptionPlans'
import { DURATION_OPTIONS, type DurationMonths } from '../subscription/subscriptionUtils'
import { planQuotas, planMonthlyPrice } from '../premium/planQuotas'
import { TERM_DISCOUNT_PCT, addDays, dueToday, formatDateLong, formatMoney } from '../premium/planMath'

type CheckoutSummaryProps = {
  plan: Plan
  term: DurationMonths
  onTermChange: (term: DurationMonths) => void
  currentPlanName: string | null
  credit: number
  hasActiveSubscription: boolean
  payTab: 'card' | 'hnt'
  // Issue BE#24 — welcher Pfad greift (siehe usePreviewUpgrade). 'checkout'
  // (Default) solange kein aktives Abo besteht bzw. die Vorschau noch nicht da ist.
  mode?: 'stripe_swap' | 'conversion' | 'checkout'
  conversionExpiresAt?: string | null
  daysRemaining?: number
}

function quotaLabel(value: number | 'unlimited' | 'unknown'): string {
  if (value === 'unlimited') return '∞'
  if (value === 'unknown') return '–'
  return String(value)
}

export function CheckoutSummary({
  plan,
  term,
  onTermChange,
  currentPlanName,
  credit,
  hasActiveSubscription,
  payTab,
  mode = 'checkout',
  conversionExpiresAt = null,
  daysRemaining,
}: CheckoutSummaryProps) {
  const { t } = useTranslation('checkout')
  const quotas = planQuotas(plan)
  const base = plan.price_eur_monthly * term
  const total = displayPrice(plan, term)
  const fromBackend = planMonthlyPrice(plan, term).fromBackend
  const discount = fromBackend ? Math.max(0, base - total) : 0
  // HNT-Tab: immer voller Laufzeitpreis (einmalige Zahlung, hängt Laufzeit
  // an) — kein Restwert/Umrechnung, egal welcher mode für den Karten-Tab gilt.
  const isConversionCard = payTab === 'card' && mode === 'conversion'
  const due = payTab === 'card' && mode === 'stripe_swap'
    ? dueToday(total, credit)
    : isConversionCard
      ? 0
      : total
  const unit = term === 1 ? t('summary.unitMonth') : t('summary.unitMonths')

  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="card card-pad" data-testid="checkout-summary">
        <div className="spread" style={{ marginBottom: 4 }}>
          <h3>{plan.name}</h3>
          {discount > 0 && (
            <span className="pill pill-accent" data-testid="summary-discount-pill">
              −{TERM_DISCOUNT_PCT[term]} %
            </span>
          )}
        </div>
        <div className="row" style={{ gap: 8, margin: '4px 0 10px', flexWrap: 'wrap' }}>
          <span className="pill pill-primary">{quotaLabel(quotas.mobile)} Mobile</span>
          <span className="pill pill-accent">{quotaLabel(quotas.iot)} IoT</span>
          {plan.api_access && <span className="pill">API & Webhooks</span>}
        </div>
        <div className="row" style={{ gap: 8, marginBottom: 12 }}>
          <span className="muted" style={{ fontSize: 12 }}>{t('summary.term')}</span>
          <div className="co-segment" role="tablist" aria-label={t('summary.term')} data-testid="term-segment">
            {DURATION_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={option === term ? 'is-active' : ''}
                onClick={() => onTermChange(option)}
                data-testid={`term-${option}`}
              >
                {option}
              </button>
            ))}
          </div>
          <span className="muted" style={{ fontSize: 12 }}>{t('summary.months')}</span>
        </div>

        <div className="sum-row">
          <span className="muted">
            {t('summary.baseLine', { plan: plan.name, term, unit, price: formatMoney(plan.price_eur_monthly, plan.currency) })}
          </span>
          <span className="v">{formatMoney(base, plan.currency)}</span>
        </div>

        {discount > 0 && (
          <div className="sum-row" data-testid="summary-discount-row">
            <span className="muted">{t('summary.discountLine', { pct: TERM_DISCOUNT_PCT[term] })}</span>
            <span className="v c-success">−{formatMoney(discount, plan.currency)}</span>
          </div>
        )}

        {payTab === 'card' && mode === 'stripe_swap' && hasActiveSubscription && credit > 0 && (
          <div className="sum-row" data-testid="summary-credit-row">
            <span className="muted">{t('summary.remainingValue', { plan: currentPlanName ?? '' })}</span>
            <span className="v c-success">−{formatMoney(credit, plan.currency)}</span>
          </div>
        )}

        {isConversionCard && (
          <div className="sum-row" data-testid="summary-conversion-row">
            <span className="muted">
              {t('summary.remainingDays', { plan: currentPlanName ?? '', days: Math.round(daysRemaining ?? 0) })}
            </span>
          </div>
        )}

        <div className="sum-row sum-total">
          <span>{t('summary.dueToday')}</span>
          <span className="v" data-testid="summary-due-today">{formatMoney(due, plan.currency)}</span>
        </div>

        <p className="muted" style={{ fontSize: 11, margin: '8px 0 0' }} data-testid="summary-note">
          {isConversionCard
            ? t('summary.conversionNote', {
                plan: plan.name,
                date: conversionExpiresAt ? formatDateLong(new Date(conversionExpiresAt)) : '—',
              })
            : payTab === 'card'
              ? t('summary.noteCard', { term, unit })
              : t('summary.noteHnt', { term, unit, date: formatDateLong(addDays(new Date(), term * 30.44)) })}
        </p>
      </div>

      <div className="card card-pad" style={{ padding: '16px 20px' }}>
        <div className="trust"><Check className="ic" size={13} /><span>{t('trust.instant')}</span></div>
        <div className="trust"><Check className="ic" size={13} /><span>{t('trust.cancelable')}</span></div>
        <div className="trust">
          <Check className="ic" size={13} />
          <span>
            <Trans
              t={t}
              i18nKey="trust.invoice"
              components={{ link: <a href="/payments" style={{ color: 'var(--primary)' }} /> }}
            />
          </span>
        </div>
        <div className="trust"><Check className="ic" size={13} /><span>{t('trust.creditApplied')}</span></div>
      </div>
    </div>
  )
}
